import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { defaultStoreUtils } from '@databases/redux';
import { deferDatabaseEntryProjection } from '@databases/redux/collab/actions';
import { applyDatabaseDiff } from '@databases/utils/redux/databases/actions';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import {
  getBackendAgentEditedEntryIds,
  getVisibleBackendAgentEditedEntryIds
} from '../backend-agent-entry-edits.ts';

export const PROGRESSIVE_ENTRY_EDIT_REVEAL_DELAY_MS = 700;

const MAX_REVEAL_TICKS = 4;

type DatabaseTransaction = Parameters<CollabManager['database']['getDiff']>[0];

interface QueuedEntryEdit {
  entryId: string;
  revision: number;
}

export type ProgressiveEntryEditRevealResult =
  | { kind: 'idle' }
  | { activeEntryId: string; kind: 'revealing' };

interface UseProgressiveEntryEditRevealOptions {
  collabManager: CollabManager | undefined;
  contentId: string;
  entryIds: readonly string[];
  isPaused: boolean;
  onEligibleBackendAgentEntryEdit?: () => void;
  resetKey: string;
}

export function useProgressiveEntryEditReveal({
  collabManager,
  contentId,
  entryIds,
  isPaused,
  onEligibleBackendAgentEntryEdit,
  resetKey
}: UseProgressiveEntryEditRevealOptions): ProgressiveEntryEditRevealResult {
  const isDatabaseBEStreamingEnabled = getExperimentValue(
    'databaseBEStreaming',
    'enabled',
    { fireExperimentExposure: false }
  );
  const dispatch = defaultStoreUtils.useDispatch();
  const previousEntryIds = useRef<readonly string[]>([]);
  const visibleEntryIds = useRef<ReadonlySet<string>>(new Set(entryIds));
  visibleEntryIds.current = new Set(entryIds);
  const revision = useRef(0);
  const isInitialRevealBatch = useRef(true);
  const revealBatchSize = useRef(1);
  const queuedEntryEditsRef = useRef<readonly QueuedEntryEdit[]>([]);
  const [queuedEntryEdits, setQueuedEntryEdits] = useState<readonly QueuedEntryEdit[]>(
    []
  );

  const updateQueuedEntryEdits = useCallback(
    (
      update: (queuedEntryEdits: readonly QueuedEntryEdit[]) => readonly QueuedEntryEdit[]
    ) => {
      setQueuedEntryEdits((currentQueuedEntryEdits) => {
        const nextQueuedEntryEdits = update(currentQueuedEntryEdits);
        if (nextQueuedEntryEdits.length === 0) {
          isInitialRevealBatch.current = true;
          revealBatchSize.current = 1;
        }
        queuedEntryEditsRef.current = nextQueuedEntryEdits;
        return nextQueuedEntryEdits;
      });
    },
    []
  );

  const projectEntryIds = useCallback(
    (projectedEntryIds: readonly string[]) => {
      if (!collabManager || projectedEntryIds.length === 0) {
        return;
      }

      dispatch(
        applyDatabaseDiff({
          contentId,
          diff: {
            fieldsOrderingChanged: false,
            entriesOrderingChanged: false,
            changedFieldIds: [],
            changedEntryIds: [...projectedEntryIds],
            threadsChanged: false,
            viewsChanged: false,
            defaultEntryChanged: false,
            othersChanged: false
          },
          collabDatabase: collabManager.database,
          isReadOnly: collabManager.isReadOnly
        })
      );
    },
    [collabManager, contentId, dispatch]
  );

  useLayoutEffect(() => {
    queuedEntryEditsRef.current = [];
    isInitialRevealBatch.current = true;
    revealBatchSize.current = 1;
    setQueuedEntryEdits([]);

    if (!collabManager) {
      previousEntryIds.current = [];
      return;
    }

    previousEntryIds.current = collabManager.database.entries;
    const onAfterTransaction = (transaction: DatabaseTransaction) => {
      const diff = collabManager.database.getDiff(transaction);
      const currentEntryIds = collabManager.database.entries;
      const editedEntryIds = getBackendAgentEditedEntryIds({
        changedEntryIds: diff.changedEntryIds,
        currentEntryIds,
        previousEntryIds: previousEntryIds.current,
        transaction
      });
      previousEntryIds.current = currentEntryIds;

      const visibleEditedEntryIds = getVisibleBackendAgentEditedEntryIds(editedEntryIds, [
        ...visibleEntryIds.current
      ]);
      const visibleEditedEntryIdSet = new Set(visibleEditedEntryIds);

      if (queuedEntryEditsRef.current.length > 0 && diff.changedEntryIds.length > 0) {
        const changedEntryIdSet = new Set(diff.changedEntryIds);
        updateQueuedEntryEdits((currentQueuedEntryEdits) =>
          currentQueuedEntryEdits.filter(
            ({ entryId }) =>
              !changedEntryIdSet.has(entryId) || visibleEditedEntryIdSet.has(entryId)
          )
        );
      }

      if (visibleEditedEntryIds.length === 0) {
        return;
      }

      onEligibleBackendAgentEntryEdit?.();
      if (!isDatabaseBEStreamingEnabled) {
        return;
      }

      deferDatabaseEntryProjection(transaction, visibleEditedEntryIds);
      updateQueuedEntryEdits((currentQueuedEntryEdits) => {
        if (currentQueuedEntryEdits.length === 0) {
          isInitialRevealBatch.current = true;
        }

        const nextQueuedEntryEdits = [...currentQueuedEntryEdits];
        const queuedIndexesByEntryId = new Map(
          currentQueuedEntryEdits.map(({ entryId }, index) => [entryId, index])
        );

        for (const entryId of visibleEditedEntryIds) {
          const queuedIndex = queuedIndexesByEntryId.get(entryId);
          const queuedEntryEdit = { entryId, revision: revision.current++ };

          if (queuedIndex === undefined) {
            queuedIndexesByEntryId.set(entryId, nextQueuedEntryEdits.length);
            nextQueuedEntryEdits.push(queuedEntryEdit);
          } else {
            nextQueuedEntryEdits[queuedIndex] = queuedEntryEdit;
          }
        }

        const queuedEntryEditCount =
          nextQueuedEntryEdits.length - (isInitialRevealBatch.current ? 1 : 0);
        revealBatchSize.current = Math.max(
          revealBatchSize.current,
          Math.ceil(queuedEntryEditCount / MAX_REVEAL_TICKS)
        );
        return nextQueuedEntryEdits;
      });
    };

    collabManager.database.doc.on('afterTransaction', onAfterTransaction);
    return () => {
      collabManager.database.doc.off('afterTransaction', onAfterTransaction);
      projectEntryIds(queuedEntryEditsRef.current.map(({ entryId }) => entryId));
      queuedEntryEditsRef.current = [];
    };
  }, [
    collabManager,
    isDatabaseBEStreamingEnabled,
    onEligibleBackendAgentEntryEdit,
    projectEntryIds,
    resetKey,
    updateQueuedEntryEdits
  ]);

  const activeBatchSize = isInitialRevealBatch.current
    ? Math.min(1, queuedEntryEdits.length)
    : Math.min(revealBatchSize.current, queuedEntryEdits.length);
  const activeEntryEdit = queuedEntryEdits[activeBatchSize - 1];

  useLayoutEffect(() => {
    if (!activeEntryEdit || isPaused) {
      return;
    }

    projectEntryIds(
      queuedEntryEdits.slice(0, activeBatchSize).map(({ entryId }) => entryId)
    );
  }, [activeBatchSize, activeEntryEdit, isPaused, projectEntryIds, queuedEntryEdits]);

  useEffect(() => {
    if (!activeEntryEdit || isPaused) {
      return;
    }

    const timeout = setTimeout(() => {
      isInitialRevealBatch.current = false;
      updateQueuedEntryEdits((currentQueuedEntryEdits) =>
        currentQueuedEntryEdits.slice(activeBatchSize)
      );
    }, PROGRESSIVE_ENTRY_EDIT_REVEAL_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [activeBatchSize, activeEntryEdit, isPaused, updateQueuedEntryEdits]);

  return activeEntryEdit
    ? { activeEntryId: activeEntryEdit.entryId, kind: 'revealing' }
    : { kind: 'idle' };
}
